<template>
    <div v-if="radar !== undefined">
        <v-toolbar>
            <v-toolbar-title
                    width="200"
            >{{ radar.name }}
            </v-toolbar-title>
            <v-spacer></v-spacer>

            <RadarSelect></RadarSelect>
        </v-toolbar>

        <v-container>
            <v-row>
                <v-col sm="12" md="6">
                    <CanvasRadar v-if="quadrant > 3"></CanvasRadar>
                    <CanvasQuadrant v-else :target-quadrant="quadrant"></CanvasQuadrant>
                    <ul class="sector-legend">
                      <li><span class="legend-color" :style="{ background: colors.sector[3] }"></span> {{ radar.sector_3 }}</li>
                      <li><span class="legend-color" :style="{ background: colors.sector[2] }"></span> {{ radar.sector_2 }}</li>
                      <li><span class="legend-color" :style="{ background: colors.sector[1] }"></span> {{ radar.sector_1 }}</li>
                      <li><span class="legend-color" :style="{ background: colors.sector[0] }"></span>{{ radar.sector_0 }}</li>
                    </ul>
                </v-col>
                <v-col sm="12" offset-md="1" md="5">
                    <v-row dense>
                        <v-col cols="12">
                            <v-card
                                outlined
                            >
                                <v-list-item>
                                    <v-list-item-avatar>
                                        <v-tooltip
                                            top
                                            :open-delay="tooltipDelay"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    icon
                                                    @click="showRadar"
                                                >
                                                    <v-icon>mdi-radar</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('controls.tooltip.open_radar') }}</span>
                                        </v-tooltip>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title class="headline">{{ radar.name }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                            <span class="country-affiliation" v-if="country">
                                                {{ $t('radar.country_affiliation') }}: {{ country.name }}
                                            </span>
                                            <v-icon size="large">mdi-account</v-icon>
                                            by {{ radar.owner_name }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <RadarFormDialog :radar="radar" v-if="isOwner"></RadarFormDialog>
                                    </v-list-item-action>
                                </v-list-item>
                                <v-card-text>
                                    <p>{{ radar.description }}</p>
                                </v-card-text>
                                <v-card-actions>
                                    <span>{{ $t('radar.perspective') }}:&nbsp;</span>
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ on }">
                                            <v-btn
                                                text
                                                min-width="150"
                                                max-width="300"
                                                v-on="on"
                                                left
                                            >
                                                {{ activeOption.text }}
                                            </v-btn>
                                        </template>
                                        <v-list>
                                            <v-list-item
                                                v-for="(option,i) in passiveOptions"
                                                :key="i"
                                                @click="quadrant=option.q"
                                            >
                                                <v-list-item-title>{{ option.text }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row v-if="currentBlob === undefined || currentBlob.id === undefined">
                        <v-col cols="12" v-if="quadrant == 0 || quadrant == 4">
                            <BlobList
                                  :radar="radar"
                                  :quadrant="0"
                                  :active="quadrant == 0"
                                  @openQuadrant="quadrant=0"
                                  @openRadar="quadrant=4"
                            ></BlobList>
                        </v-col>
                        <v-col cols="12" v-if="quadrant == 1 || quadrant == 4">
                            <BlobList
                                    :radar="radar"
                                    :quadrant="1"
                                    :active="quadrant == 1"
                                    @openQuadrant="quadrant=1"
                                    @openRadar="quadrant=4"
                            ></BlobList>
                        </v-col>
                        <v-col cols="12" v-if="quadrant == 2 || quadrant == 4">
                            <BlobList
                                    :radar="radar"
                                    :quadrant="2"
                                    :active="quadrant == 2"
                                    @openQuadrant="quadrant=2"
                                    @openRadar="quadrant=4"
                            ></BlobList>
                        </v-col>
                        <v-col cols="12" v-if="quadrant >= 3">
                            <BlobList
                                    :radar="radar"
                                    :quadrant="3"
                                    :active="quadrant == 3"
                                    @openQuadrant="quadrant=3"
                                    @openRadar="quadrant=4"
                            ></BlobList>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col cols="13">
                            <BlobDetails :blob="currentBlob"></BlobDetails>
                        </v-col>
                    </v-row>

                </v-col>
            </v-row>
        </v-container>
    </div>

</template>

<script>
  import { mapGetters } from 'vuex'
  import { blobsLoader } from '../mixins/blobsLoader'
  import CanvasRadar from '@/components/Canvas/CanvasRadar'
  import CanvasQuadrant from '@/components/Canvas/CanvasQuadrant'
  import BlobList from '@/components/Blob/BlobList'
  import BlobDetails from '../components/Blob/BlobDetails'
  import { settings, canvas } from '../settings/settings'
  import RadarFormDialog from '../components/radar/RadarFormDialog'
  import RadarSelect from '../components/radar/RadarSelect'

  export default {
    name: 'Radar',
    mixins: [blobsLoader],
    components: { RadarSelect, RadarFormDialog, CanvasRadar, CanvasQuadrant, BlobList, BlobDetails },
    props: {
      radarId: {
        required: true
      },
      quadrantId: {
        type: Number,
        default: 4,
      }
    },

    data: () => ({
      ...settings,
      ...canvas,
      radarFormDialog: false
    }),

    computed: {
      ...mapGetters({
        userId: 'user/userId',
        isAdmin: 'user/isAdmin',
        radar: 'radars/getCurrentRadar',
        currentQuadrant: 'radars/getCurrentQuadrant',
        currentBlob: 'blobs/getCurrentBlob',
        isLoading: 'isLoading',
        getCountryByISO: 'countries/getCountryByISO'
      }),

      isOwner() {
        return this.radar.owner_id === this.userId
      },

      country () {
        return this.getCountryByISO(this.radar.country)
      },
      quadrant: {
        get () {
          return this.currentQuadrant
        },
        set (val) {
          this.$store.commit('radars/setCurrentQuadrant', val)
        }
      },
      options () {
        return [
          { text: this.$t('radar.tabs.radar'), q: 4 },
          { text: this.radar.quadrant_0, q: 0 },
          { text: this.radar.quadrant_1, q: 1 },
          { text: this.radar.quadrant_2, q: 2 },
          { text: this.radar.quadrant_3, q: 3 }
        ]
      },
      activeOption () {
        return this.options.find(o => o.q === this.quadrant)
      },
      passiveOptions () {
        return this.options.filter(o => o.q !== this.quadrant)
      }
    },
    created () {

      if(this.$store.getters['radars/getRadar'](+this.radarId) === undefined) {
        if(this.isAdmin) {
          this.openForeignRadar(this.radarId)
        } else {
          this.$router.push({ name: 'dashboard' }).catch(() => {})
        }
      } else {
        // remove if not needed
        this.$store.commit('radars/setCurrentRadarId', +this.radarId)
      }
    },

    methods: {
      showRadar () {
        this.quadrant = 4
        this.$store.commit('blobs/clearCurrentBlobId')
        // this.$emit('openQuadrant', this.blob.quadrant)
        this.$router.push({ name: 'radar', params: { quadrantId: 4 } }).catch(() => {})
      },

      openForeignRadar (id) {
        this.$store.commit('blobs/clearCurrentBlobId')
        this.$store.commit('radars/setCurrentRadarId', +id)
        this.$store.dispatch('radars/loadForeignRadar', +id)
        this.$store.dispatch('blobs/loadRadarBlobs', +id)
      }
    },
    watch: {
      quadrantId () {
        this.quadrant = this.quadrantId
      },
      $route (to) {
        this.$store.commit('radars/setCurrentRadarId', +to.params.radarId)
      },
      currentBlob (blob) {
        if (blob.id !== undefined) {
          this.quadrant = blob.quadrant
        }
      }
    }

  }
</script>

<style lang="scss">
    .country-affiliation {
        display: block;
    }

    ul.sector-legend {
        margin-top: 30px;
        padding: 0;
        list-style: none;

        li {
            float: left;
            margin-right: 20px;
        }

        .legend-color {
            display: inline-block;
            width: 15px;
            height: 15px;
            margin-right: 5px;
            border: 1px solid #cccccc;
        }
    }
</style>
